import { ROCKETCHAT_DEMO_DOMAIN, ROCKETCHAT_DEV_DOMAIN, ROCKETCHAT_TEST_DOMAIN } from "@eventr/common";
import { reactAppStage } from "../../utils/reactAppStage";

/**
 * Which server the chat talks to: each org's own Rocket.Chat container, or spchat.
 *
 * spchat speaks Rocket.Chat's REST and DDP, so the chat code is the same either way; only the
 * host and where the credentials come from change. With spchat, every org is reached through
 * one host (the sealed session names the org) and spchat decides per org whether to answer
 * itself or relay to the org's container - so switching an org over is done on the server, not
 * here, and this switch can be flipped at any time.
 *
 * The default is the build's `VITE_CHAT_BACKEND` (`spchat` when unset); the dev menu's Env
 * Settings overrides it per device, kept in `localStorage["CHAT_BACKEND"]`.
 */
export type ChatBackend = "rocketchat" | "spchat";

export const CHAT_BACKEND_KEY = "CHAT_BACKEND";

export const chatBackend = (): ChatBackend => {
  const value = localStorage.getItem(CHAT_BACKEND_KEY) ?? import.meta.env.VITE_CHAT_BACKEND;
  return value === "rocketchat" ? "rocketchat" : "spchat";
};

export const setChatBackend = (backend: ChatBackend) => {
  localStorage.setItem(CHAT_BACKEND_KEY, backend);
};

/** spchat's origin for this stage: `spchat.c.<stage domain>`, or the local stack's `:3041`. */
export const spchatUrl = (): string => {
  switch (reactAppStage()) {
    case "dev":
      return `https://spchat.${ROCKETCHAT_DEV_DOMAIN}`;
    case "test":
      return `https://spchat.${ROCKETCHAT_TEST_DOMAIN}`;
    case "local":
      return "http://localhost:3041";
    case "demo":
    default:
      return `https://spchat.${ROCKETCHAT_DEMO_DOMAIN}`;
  }
};

/** spchat's DDP endpoint, the same path Rocket.Chat serves it on. */
export const spchatWebsocket = (): string => `${spchatUrl().replace(/^http/, "ws")}/websocket`;
